import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Layout, Menu, Dropdown, Button, Drawer } from 'antd';
import { GlobalOutlined, MenuOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useCompanyInfo from '../hooks/useCompanyInfo';
import { getImageUrl } from '../services/api';

const { Header, Content, Footer } = Layout;

const languages = [
  { key: 'en', label: 'English' },
  { key: 'zh', label: '中文' },
];

function SiteLogo({ logo, companyName, size = 'header' }) {
  const isHeader = size === 'header';
  const height = isHeader ? 58 : 44;

  if (logo) {
    return (
      <img
        src={getImageUrl(logo)}
        alt={companyName || 'Huanyu Supply'}
        style={{ height, maxWidth: isHeader ? 280 : 200, objectFit: 'contain' }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <img
        src="/images/logo.png"
        alt="Huanyu Supply"
        style={{
          height: isHeader ? 58 : 44,
          maxWidth: isHeader ? 280 : 200,
          objectFit: 'contain',
        }}
      />
    </div>
  );
}

export default function MainLayout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const location = useLocation();
  const { info: companyInfo } = useCompanyInfo();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const currentLang = lang || i18n.language || 'en';
  const companyName = currentLang === 'zh'
    ? (companyInfo?.company_name_zh || companyInfo?.company_name)
    : companyInfo?.company_name;

  useEffect(() => {
    if (lang && lang !== i18n.language) i18n.changeLanguage(lang);
  }, [lang, i18n]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setScrolled(false);
  }, [location.pathname]);

  const switchLanguage = (newLang) => {
    i18n.changeLanguage(newLang);
    const path = location.pathname;
    const langPattern = /^\/(en|zh)(\/|$)/;
    const newPath = langPattern.test(path)
      ? path.replace(langPattern, `/${newLang}$2`)
      : `/${newLang}${path}`;
    navigate(newPath);
  };

  const getPath = (path) => `/${currentLang}${path}`;

  const navItems = [
    { key: 'home', label: <Link to={getPath('/')} className="nav-link">{t('nav.home')}</Link> },
    { key: 'products', label: <Link to={getPath('/products')} className="nav-link">{t('nav.products')}</Link> },
    { key: 'about', label: <Link to={getPath('/about')} className="nav-link">{t('nav.about')}</Link> },
    { key: 'contact', label: <Link to={getPath('/contact')} className="nav-link">{t('nav.contact')}</Link> },
  ];

  const langMenu = {
    items: languages.map((l) => ({
      key: l.key,
      label: l.label,
      onClick: () => switchLanguage(l.key),
    })),
  };

  const currentLangLabel = languages.find((l) => l.key === currentLang)?.label || 'English';
  const address = companyInfo?.address || t('footer.address');
  const email = companyInfo?.email || t('footer.email_value');
  const phone = companyInfo?.phone || t('footer.phone_value');
  const brandDesc = companyInfo?.description || t('footer.brand_desc');

  return (
    <Layout key={location.pathname} style={{ minHeight: '100vh', background: '#fff' }}>
      <Header className={`site-header ${scrolled ? 'scrolled' : ''}`}>
        <Link to={getPath('/')} className="site-logo-link">
          <SiteLogo logo={companyInfo?.logo} companyName={companyName} />
        </Link>

        <div className="desktop-nav">
          <Menu mode="horizontal" items={navItems} className="site-nav-menu" />
          <Dropdown menu={langMenu} placement="bottomRight">
            <Button type="text" icon={<GlobalOutlined />} className="lang-btn">
              {currentLangLabel}
            </Button>
          </Dropdown>
        </div>

        <Button
          className="mobile-menu-btn"
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setMobileMenuOpen(true)}
        />
      </Header>

      <Drawer
        title={t('layout.mobile_menu')}
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
      >
        <Menu mode="vertical" items={navItems} onClick={() => setMobileMenuOpen(false)} />
        <div style={{ padding: '16px 0' }}>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>{t('layout.language')}</p>
          {languages.map((l) => (
            <Button
              key={l.key}
              type={currentLang === l.key ? 'primary' : 'text'}
              block
              style={{ textAlign: 'left', marginBottom: 4 }}
              onClick={() => { switchLanguage(l.key); setMobileMenuOpen(false); }}
            >
              {l.label}
            </Button>
          ))}
        </div>
      </Drawer>

      <Content style={{ marginTop: 0 }}>
        <Outlet />
      </Content>

      <Footer className="site-footer">
        <div className="container">
          <div className="footer-grid">
            <div>
              <div style={{ marginBottom: 24 }}>
                <SiteLogo logo={companyInfo?.logo} companyName={companyName} size="footer" />
              </div>
              <p className="footer-desc">{brandDesc}</p>
            </div>
            <div>
              <h4>{t('footer.quick_links')}</h4>
              <div className="footer-links">
                <Link to={getPath('/products')}>{t('nav.products')}</Link>
                <Link to={getPath('/about')}>{t('nav.about')}</Link>
                <Link to={getPath('/contact')}>{t('nav.contact')}</Link>
              </div>
            </div>
            <div>
              <h4>{t('footer.contact_info')}</h4>
              <div className="footer-links">
                <span><EnvironmentOutlined /> {address}</span>
                <span><MailOutlined /> {email}</span>
                <span><PhoneOutlined /> {phone}</span>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            © {new Date().getFullYear()} {companyName || 'Yancheng Huanyu Import & Export Co., Ltd.'} {t('footer.rights')}
          </div>
        </div>
      </Footer>
    </Layout>
  );
}
